import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { DynamicField, ConsentCheckbox, Language } from '@/shared/types';

export interface SectionPresetField {
  id: string;
  key: string;
  label: Record<Language, string>;
  type: DynamicField['type'];
  required: boolean;
}

export interface SectionPreset {
  id: string;
  owner_id: string;
  name: string;
  fields: SectionPresetField[];
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

const emptyML = (): Record<Language, string> => ({ fr: '', en: '', es: '' });

function mkPresetField(
  key: string,
  type: DynamicField['type'],
  labelFr: string,
  labelEn: string,
  labelEs: string,
  required = false,
): SectionPresetField {
  return { id: crypto.randomUUID(), key, type, required, label: { fr: labelFr, en: labelEn, es: labelEs } };
}

const SYSTEM_SEEDS: Array<{ name: string; fields: SectionPresetField[] }> = [
  {
    name: 'Infos client',
    fields: [
      mkPresetField('first_name', 'text', 'Prénom', 'First name', 'Nombre', true),
      mkPresetField('last_name', 'text', 'Nom', 'Last name', 'Apellido', true),
      mkPresetField('email', 'email', 'Email', 'Email', 'Email', true),
      mkPresetField('phone', 'tel', 'Téléphone', 'Phone', 'Teléfono'),
      mkPresetField('birth_date', 'date', 'Date de naissance', 'Date of birth', 'Fecha de nacimiento', true),
      mkPresetField('address', 'address', 'Adresse', 'Address', 'Dirección'),
    ],
  },
  {
    name: 'Déclaration médicale',
    fields: [
      mkPresetField('MEDICAL_DECL', 'textarea', 'Déclaration médicale', 'Medical declaration', 'Declaración médica'),
    ],
  },
  {
    name: 'Consentements RGPD',
    fields: [
      mkPresetField('RGPD_DATA', 'textarea', 'Consentement RGPD', 'GDPR consent', 'Consentimiento RGPD'),
      mkPresetField('RGPD_CONTACT', 'textarea', 'Consentement suivi', 'Follow-up consent', 'Consentimiento seguimiento'),
    ],
  },
  {
    name: 'Signature + date',
    fields: [
      mkPresetField('SIGNATURE', 'signature', 'Signature du client', 'Client signature', 'Firma del cliente', true),
      mkPresetField('date_today', 'date', 'Date', 'Date', 'Fecha', true),
    ],
  },
];

export function useSectionPresets() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const seedDone = useRef(false);

  const query = useQuery({
    queryKey: ['section-presets', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<SectionPreset[]> => {
      const { data, error } = await (supabase as any)
        .from('template_section_presets')
        .select('*')
        .eq('owner_id', user!.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data || []).map((row: any) => ({
        ...row,
        fields: (row.fields as SectionPresetField[]) || [],
      }));
    },
  });

  useEffect(() => {
    if (seedDone.current) return;
    if (!query.data) return;
    if (query.data.length > 0) { seedDone.current = true; return; }
    seedDone.current = true;
    const toInsert = SYSTEM_SEEDS.map(s => ({
      owner_id: user!.id,
      name: s.name,
      fields: s.fields,
      is_system: true,
    }));
    (supabase as any)
      .from('template_section_presets')
      .insert(toInsert)
      .select()
      .then(() => queryClient.invalidateQueries({ queryKey: ['section-presets'] }));
  }, [query.data]);

  const createPreset = useMutation({
    mutationFn: async ({ name, fields }: { name: string; fields: SectionPresetField[] }) => {
      const { data, error } = await (supabase as any)
        .from('template_section_presets')
        .insert({ owner_id: user!.id, name, fields, is_system: false })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['section-presets'] }),
  });

  const updatePreset = useMutation({
    mutationFn: async ({ id, name, fields }: { id: string; name: string; fields: SectionPresetField[] }) => {
      const { error } = await (supabase as any)
        .from('template_section_presets')
        .update({ name, fields, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('owner_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['section-presets'] }),
  });

  const deletePreset = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('template_section_presets')
        .delete()
        .eq('id', id)
        .eq('owner_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['section-presets'] }),
  });

  return {
    ...query,
    presets: query.data || [],
    createPreset,
    updatePreset,
    deletePreset,
  };
}
